import { List } from "immutable";

import { type BaseSpec } from "@moment/api-collab/api-types";
import { type Store } from "@moment/api-collab/differential";
import {
	type Action,
	hydrateAction,
	rawActionSchema,
} from "@moment/api-collab/differential/QueryEvaluator/Action";
import { type DatabaseTables } from "@moment/api-collab/differential/types";
import { Logger } from "@moment/logging";

import { RebootContext, type RebootInstance, RebootStore, type TableChangesResponse } from ".";

const logger = new Logger("rebootSubscriber");

//const debuglog = (_msg: string, ...args: any[]) => console.log(`[rebootSubscriber] ${_msg}`, ...args);
const debuglog = (_msg: string, ..._args: any[]) => {};

export class RebootSubscriber {
	private _subscriber: Promise<void>;
	private _abortController: AbortController;
	private _uuid: string;

	constructor(
		reboot: RebootInstance,
		private readonly _store: Store<DatabaseTables>,
		baseSpec: BaseSpec,
		private readonly _sendStreamError: (error: Error) => void
	) {
		this._uuid = crypto.randomUUID();
		this._abortController = new AbortController();
		const signal = this._abortController.signal;

		// Worst case scenario, we use fromVersion = 0. In this case the
		// reboot server will send us all the updates it knows about, which may
		// be quite a bit of data, but it should handle fine.
		let fromVersion: number = Math.max(this._store.latestEpoch - 1, 0);

		this._subscriber = (async () => {
			const [generator, updateRequest] = await reboot.store
				.reactively()
				.tableChanges(reboot.context, { fromVersion }, { signal });

			for await (const value of this._wrappedGenerator(generator)) {
				debuglog(`received event from generator`, {
					fromVersion,
					value,
					uuid: this._uuid,
				});

				const receivedActions = parseActions(value);
				await this._attemptReconciliation(receivedActions);

				const lastHandledEpoch = getLastReceivedEpoch(receivedActions);

				debuglog(`reconciled update from reboot`, {
					fromVersion,
					serverEpoch: lastHandledEpoch,
					value,
					actions: receivedActions.toArray(),
					uuid: this._uuid,
				});

				if (lastHandledEpoch) {
					fromVersion = lastHandledEpoch;
					updateRequest({ fromVersion });
				}
			}
		})();
	}

	cancel() {
		debuglog(`cancelling this subscriber...`, { uuid: this._uuid });
		this._abortController.abort(
			new Error(`client requested cancellation of this subscriber uuid=${this._uuid}`)
		);
	}

	private async *_wrappedGenerator<T>(generator: AsyncGenerator<T, unknown, unknown>) {
		try {
			yield* generator;
		} catch (e) {
			logger.error(`error getting next update from reboot generator`, {
				error: e,
				uuid: this._uuid,
			});
			// Note: at some point I had the _abortController handling multiple streams,
			// now that we don't, it should be safe to get rid of this abort, but it also
			// seemed plausible that the multiplexed streams could come back.
			this._abortController.abort(
				new Error(`error getting next update from reboot generator uuid=${this._uuid}`)
			);
			this._sendStreamError(e as Error);
			throw e;
		}
	}

	private async _attemptReconciliation(actions: List<Action>) {
		try {
			debuglog(`attempting to reconcile from tableChanges update`, {
				actions: actions.toArray(),
				uuid: this._uuid,
			});
			await this._store.reconcileActions(actions);
		} catch (e) {
			logger.error(`error reconciling update from reboot into local store`, {
				error: e,
				uuid: this._uuid,
			});
			debuglog(`error reconciling update from reboot into local store`, {
				error: e,
				uuid: this._uuid,
			});
			// does NOT abort the reboot connections
		}
	}
}

const parseActions = (value: TableChangesResponse): List<Action> => {
	return List(value.changesJson).map((protoc) => {
		const parsed = rawActionSchema.parse(protoc.toJson());
		return hydrateAction(parsed);
	});
};

// may return undefined - if so, the fromVersion will NOT be updated,
// which in weird cases could cause events to be re-recevied, which
// reconciliation will need to be resilient to.
const getLastReceivedEpoch = (receivedActions: List<Action>): number | undefined => {
	const lastAction = receivedActions.last();
	if (!lastAction) {
		return undefined;
	}
	return lastAction.type === "queryRegistration"
		? lastAction.desiredStartEpoch
		: lastAction.desiredApplyEpoch;
};
